/**
 * Copyright © Veeam Software Group GmbH.
 */

import { UserRoleRepresentation } from '@veeam-vspc/models/web-controllers';
import { PortalFiltersIds } from '@veeam-vspc/shared/addons';
import { ExportActionIds } from '@veeam-vspc/shared/components';
import { ConfigStates } from '@veeam-vspc/shared/core';

import type { UserConfigData } from '@veeam-vspc/shared/core';

import {
    ConfigActionIds,
    ComputersActionIds,
    ConfigAdvancedFilterItemsIds,
    ConfigSectionIds,
    ConfigItemIds,
} from 'core/enums';
import { RouteIds, RoutePaths } from '../../enums';
import { allConfigsRoutes, allCustomRoutes, allHomeRoutes } from '../routes';
import { AppPortalLayout } from 'views/components/layouts/AppPortalLayout';
import { AppPortalBodyLayout } from 'views/components/layouts/AppPortalBodyLayout';

import type { VspcLang } from '../../languages';

export const resellerUserConfig: UserConfigData<typeof UserRoleRepresentation.ResellerUser, ConfigSectionIds | PortalFiltersIds> = {
    role: UserRoleRepresentation.ResellerUser,
    routes: [
        allCustomRoutes[RouteIds.Login],
        {
            path: '',
            component: AppPortalLayout,
            items: [
                {
                    path: RoutePaths.Home,
                    component: AppPortalBodyLayout,
                    items: [
                        allHomeRoutes[RouteIds.Overview],
                        allHomeRoutes[RouteIds.JobsSummary],
                        allHomeRoutes[RouteIds.ActiveAlarms],
                        allHomeRoutes[RouteIds.ProtectedDataSummary],
                        allHomeRoutes[RouteIds.BackupJobs],
                        allHomeRoutes[RouteIds.FailoverPlans],
                        allHomeRoutes[RouteIds.ProtectedData],
                        allHomeRoutes[RouteIds.Companies],
                        allHomeRoutes[RouteIds.ManagedComputers],
                        {
                            ...allHomeRoutes[RouteIds.BillingSummary],
                            navNameKey: 'RESOURCES' as VspcLang['RESOURCES'],
                        },
                        allHomeRoutes[RouteIds.Reports],
                    ],
                },
                {
                    path: RoutePaths.Configs,
                    component: AppPortalBodyLayout,
                    items: [
                        allConfigsRoutes[RouteIds.RolesUsersReseller],
                    ],
                },
            ],
        },
        allCustomRoutes[RouteIds.Api],
        {
            path: '*',
            redirectTo: RoutePaths.NotFound,
        },
    ],
    sections: [
        {
            id: ConfigSectionIds.BackupJobsComputersManagedByConsole,
            defaultActionsState: ConfigStates.Hidden,
            actions: {
                [ExportActionIds.ExportFillSpace]: ConfigStates.Visible,
                [ExportActionIds.ExportToGroup]: ConfigStates.Visible,
                [ExportActionIds.ExportCSV]: ConfigStates.Visible,
                [ExportActionIds.ExportXML]: ConfigStates.Visible,
            },
        },
        {
            id: ConfigSectionIds.BackupJobsComputersManagedByBackupServer,
            defaultActionsState: ConfigStates.Hidden,
            actions: {
                [ExportActionIds.ExportFillSpace]: ConfigStates.Visible,
                [ExportActionIds.ExportToGroup]: ConfigStates.Visible,
                [ExportActionIds.ExportCSV]: ConfigStates.Visible,
                [ExportActionIds.ExportXML]: ConfigStates.Visible,
            },
        },
        {
            id: ConfigSectionIds.BackupJobsVirtualMachinesVirtualInfrastructure,
            defaultActionsState: ConfigStates.Hidden,
            actions: {
                [ExportActionIds.ExportFillSpace]: ConfigStates.Visible,
                [ExportActionIds.ExportToGroup]: ConfigStates.Visible,
                [ExportActionIds.ExportCSV]: ConfigStates.Visible,
                [ExportActionIds.ExportXML]: ConfigStates.Visible,
            },
        },
        {
            id: ConfigSectionIds.BackupJobsFileSharesLocal,
            defaultActionsState: ConfigStates.Hidden,
            actions: {
                [ExportActionIds.ExportFillSpace]: ConfigStates.Visible,
                [ExportActionIds.ExportToGroup]: ConfigStates.Visible,
                [ExportActionIds.ExportCSV]: ConfigStates.Visible,
                [ExportActionIds.ExportXML]: ConfigStates.Visible,
            },
        },
        {
            id: ConfigSectionIds.BackupJobsDataBackupObjectStorage,
            defaultActionsState: ConfigStates.Hidden,
            actions: {
                [ExportActionIds.ExportFillSpace]: ConfigStates.Visible,
                [ExportActionIds.ExportToGroup]: ConfigStates.Visible,
                [ExportActionIds.ExportCSV]: ConfigStates.Visible,
                [ExportActionIds.ExportXML]: ConfigStates.Visible,
            },
        },
        {
            id: ConfigSectionIds.ProtectedDataComputers,
            actions: {
                [ComputersActionIds.FileLevelRestorePortalTBD]: ConfigStates.Hidden,
            },
        },
        {
            id: ConfigSectionIds.BackupJobsCloudDatabases,
            defaultActionsState: ConfigStates.Hidden,
            actions: {
                [ExportActionIds.ExportFillSpace]: ConfigStates.Visible,
                [ExportActionIds.ExportToGroup]: ConfigStates.Visible,
                [ExportActionIds.ExportCSV]: ConfigStates.Visible,
                [ExportActionIds.ExportXML]: ConfigStates.Visible,
            },
        },
        {
            id: ConfigSectionIds.BackupJobsVirtualMachinesPublicClouds,
            defaultActionsState: ConfigStates.Hidden,
            actions: {
                [ExportActionIds.ExportFillSpace]: ConfigStates.Visible,
                [ExportActionIds.ExportToGroup]: ConfigStates.Visible,
                [ExportActionIds.ExportCSV]: ConfigStates.Visible,
                [ExportActionIds.ExportXML]: ConfigStates.Visible,
            },
        },
        {
            id: ConfigSectionIds.BackupJobsFileSharesPublicClouds,
            defaultActionsState: ConfigStates.Hidden,
            actions: {
                [ExportActionIds.ExportFillSpace]: ConfigStates.Visible,
                [ExportActionIds.ExportToGroup]: ConfigStates.Visible,
                [ExportActionIds.ExportCSV]: ConfigStates.Visible,
                [ExportActionIds.ExportXML]: ConfigStates.Visible,
            },
        },
        {
            id: ConfigSectionIds.ProtectedDataCloudDatabases,
            defaultActionsState: ConfigStates.Hidden,
            actions: {
                [ExportActionIds.ExportFillSpace]: ConfigStates.Visible,
                [ExportActionIds.ExportToGroup]: ConfigStates.Visible,
                [ExportActionIds.ExportCSV]: ConfigStates.Visible,
                [ExportActionIds.ExportXML]: ConfigStates.Visible,
            },
        },
        {
            id: ConfigSectionIds.ProtectedDataFileSharesPublicClouds,
            defaultActionsState: ConfigStates.Hidden,
            actions: {
                [ExportActionIds.ExportFillSpace]: ConfigStates.Visible,
                [ExportActionIds.ExportToGroup]: ConfigStates.Visible,
                [ExportActionIds.ExportCSV]: ConfigStates.Visible,
                [ExportActionIds.ExportXML]: ConfigStates.Visible,
            },
        },
        {
            id: ConfigSectionIds.ActiveAlarmsAlarms,
            defaultActionsState: ConfigStates.Hidden,
            actions: {
                [ExportActionIds.ExportFillSpace]: ConfigStates.Visible,
                [ExportActionIds.ExportToGroup]: ConfigStates.Visible,
                [ExportActionIds.ExportCSV]: ConfigStates.Visible,
                [ExportActionIds.ExportXML]: ConfigStates.Visible,
            },
        },
        {
            id: ConfigSectionIds.ReportsAllReports,
            defaultActionsState: ConfigStates.Hidden,
            actions: {
                [ConfigActionIds.ActionToolbarViewReports]: ConfigStates.Visible,
            },
        },
        {
            id: ConfigSectionIds.InvoicesConfiguration,
            actions: {
                [ConfigActionIds.FilterToolbarType]: ConfigStates.Hidden,
            },
        },
        {
            id: ConfigSectionIds.InvoicesAllInvoices,
            defaultActionsState: ConfigStates.Hidden,
            actions: {
                [ConfigActionIds.FilterToolbarCompanySearch]: ConfigStates.Visible,
                [ConfigActionIds.FilterToolbarStatus]: ConfigStates.Visible,
                [ConfigActionIds.FilterToolbarDatePeriod]: ConfigStates.Visible,
            },
            columns: {
                companyName: ConfigStates.Hidden,
            },
        },
        {
            id: ConfigSectionIds.ActiveAlarmsAlarmsFilterToolbarAdvancedFilter,
            advancedFilters: {
                [ConfigAdvancedFilterItemsIds.AlarmProductVspc]: ConfigStates.Hidden,
                [ConfigAdvancedFilterItemsIds.AlarmProductCloudConnect]: ConfigStates.Hidden,
                [ConfigAdvancedFilterItemsIds.AlarmScope]: ConfigStates.Hidden,
            },
        },
        {
            id: PortalFiltersIds.PortalFiltersSection,
            items: {
                [PortalFiltersIds.SitesPortalFilter]: ConfigStates.Hidden,
            },
        },
        {
            id: ConfigSectionIds.FailoverPlansFailoverPlans,
            defaultActionsState: ConfigStates.Hidden,
            actions: {
                [ExportActionIds.ExportFillSpace]: ConfigStates.Visible,
                [ExportActionIds.ExportToGroup]: ConfigStates.Visible,
                [ExportActionIds.ExportCSV]: ConfigStates.Visible,
                [ExportActionIds.ExportXML]: ConfigStates.Visible,
            },
        },
        {
            id: ConfigSectionIds.ReportsFilterToolbarAdvancedFilter,
            advancedFilters: {
                [ConfigAdvancedFilterItemsIds.ReportsManagingCompany]: ConfigStates.Hidden,
            },
        },
        {
            id: ConfigSectionIds.BackupJobsVirtualMachinesVirtualInfrastructureAdvancedFilter,
            advancedFilters: {
                [ConfigAdvancedFilterItemsIds.BackupJobsVMsVirtualInfrastructureBackupServerType]: ConfigStates.Hidden,
                [ConfigAdvancedFilterItemsIds.BackupJobsVMsVirtualInfrastructurePlatformType]: ConfigStates.Hidden,
            },
        },
        {
            id: ConfigSectionIds.Companies,
            items: {
                [ConfigItemIds.CompaniesWizardVBRServersActionToolbar]: ConfigStates.Hidden,
                [ConfigItemIds.CompaniesWizardVBRRepositoriesActionToolbar]: ConfigStates.Hidden,
            },
        },
        {
            id: ConfigSectionIds.CompaniesLocations,
            actions: {
                [ConfigActionIds.New]: ConfigStates.Hidden,
                [ConfigActionIds.Edit]: ConfigStates.Hidden,
                [ConfigActionIds.Remove]: ConfigStates.Hidden,
            },
        },
    ],
};
